import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { resting, darfAnrufAufreissen, mergeServerNotifications } from './island-state';
import type { AnrufKarte } from '../../lib/telefonie/anrufKarte';
import type { FormMeta } from './form/form-stil';

export type { FormMeta };

/* ------------------------------- types ------------------------------- */

export type IslandMode = 'idle' | 'live' | 'toast' | 'anruf' | 'deck' | 'form';
export type DeckPage = 'controls' | 'notifications' | 'chat' | 'zeit' | 'telefon' | 'search' | 'widgets';
export type Tone = 'info' | 'success' | 'error';
export type ActivityStatus = 'running' | 'success' | 'error';

export interface IslandActivity {
  id: string;
  label: string;
  /** 0–100; undefined = indeterminate */
  progress?: number;
  status: ActivityStatus;
}

export interface IslandNotification {
  id: string;
  title: string;
  body?: string;
  tone: Tone;
  at: number;
  read: boolean;
  /** optional deep-link target — makes the notification + toast clickable */
  href?: string;
  /** 'server' = aus der notifications-Tabelle (persistent), sonst Session-Toast. */
  source?: 'session' | 'server';
}

export interface IslandToast {
  id: string;
  title: string;
  /** Zweite Zeile — bei Chat-Nachrichten die Textvorschau unter Absender/Gruppe. */
  body?: string;
  tone: Tone;
  href?: string;
}

export interface IslandState {
  mode: IslandMode;
  /** which page of the swipeable deck is showing (when mode === 'deck') */
  deckPage: DeckPage;
  activities: IslandActivity[];
  notifications: IslandNotification[];
  toast: IslandToast | null;
  /** controls registered by the current page (rendered as the deck's 'controls' page) */
  pageControls: ReactNode | null;
  pageLabel: string | null;
  /** measured content height of the controls page, in px — drives the island's
   *  deck-mode height so pages with more rows don't get clipped. null = use default. */
  controlsContentHeight: number | null;
  /** a form pushed by a page (rendered in 'form' mode) */
  pageForm: ReactNode | null;
  formTitle: string | null;
  formWidth: number | null;
  /** Kopf-Angaben des Formulars (Icon, Farbe, Badge, Untertitel, Pflicht-Hinweis) – optional. */
  formMeta: FormMeta | null;
  /** Gemessene Inhaltshöhe des Formulars (Kopf + Inhalt) in px – die Pille wächst damit bis SIZE.form.h. */
  formContentHeight: number | null;
  /** Laufender oder eben beendeter Anruf — treibt den 'anruf'-Modus. */
  anruf: AnrufKarte | null;
}

export interface IslandActions {
  expand: () => void;
  collapse: () => void;
  openSearch: () => void;
  openPage: () => void;
  openDeck: (page: DeckPage) => void;
  setDeckPage: (page: DeckPage) => void;
  openForm: (form: ReactNode, title: string, width?: number, meta?: FormMeta) => void;
  closeForm: () => void;
  setFormContentHeight: (h: number | null) => void;
  setPage: (controls: ReactNode | null, label: string | null) => void;
  setControlsContentHeight: (h: number | null) => void;
  notify: (n: { title: string; body?: string; tone?: Tone; href?: string }) => void;
  startActivity: (a: { id: string; label: string; progress?: number }) => void;
  updateActivity: (id: string, patch: Partial<Omit<IslandActivity, 'id'>>) => void;
  finishActivity: (id: string, status: 'success' | 'error', label?: string) => void;
  markAllRead: () => void;
  clearNotifications: () => void;
  /** Persistente (DB-)Benachrichtigungen in die Liste spiegeln — ersetzt die
   *  bisherigen 'server'-Einträge, behält die Session-Toasts. */
  setServerNotifications: (items: IslandNotification[]) => void;
  /** Anrufkarte zeigen bzw. aktualisieren. Verdrängt Ruhezustände, aber NICHT
   *  ein offenes Formular oder Deck — niemandem soll mitten in der Eingabe die
   *  Maske weggezogen werden. */
  zeigeAnruf: (karte: AnrufKarte) => void;
  schliesseAnruf: () => void;
  /** Navigations-Callback aus App.tsx — für Objekte die pendingObjectId-Logik. */
  onSearchNavigate?: (path: string, id: string, type: string) => void;
}

/* ----------------------------- contexts ------------------------------ */

// State and actions are split into two contexts on purpose: the actions object
// is built once and never changes, so components that only trigger actions
// (pages registering controls, forms pushing toasts) never re-render when the
// island's visual state changes.
const IslandStateContext = createContext<IslandState | null>(null);
const IslandActionsContext = createContext<IslandActions | null>(null);

const TOAST_MS = 3800;
const ACTIVITY_LINGER_MS = 2400;

/* ----------------------------- provider ------------------------------ */

export function IslandProvider({
  children,
  onSearchNavigate,
}: {
  children: ReactNode;
  onSearchNavigate?: (path: string, id: string, type: string) => void;
}) {
  const [state, setState] = useState<IslandState>({
    mode: 'idle',
    deckPage: 'notifications',
    activities: [],
    notifications: [],
    toast: null,
    pageControls: null,
    pageLabel: null,
    controlsContentHeight: null,
    pageForm: null,
    formTitle: null,
    formWidth: null,
    formMeta: null,
    formContentHeight: null,
    anruf: null,
  });
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activityTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const expand = useCallback(
    () => setState((s) => ({ ...s, mode: 'deck', deckPage: 'notifications' })),
    [],
  );
  const openSearch = useCallback(
    () => setState((s) => ({ ...s, mode: 'deck', deckPage: 'search' })),
    [],
  );
  const openPage = useCallback(
    () => setState((s) => ({
      ...s,
      mode: 'deck',
      deckPage: s.pageControls ? 'controls' : 'notifications',
    })),
    [],
  );
  const setDeckPage = useCallback(
    (page: DeckPage) => setState((s) => ({ ...s, deckPage: page })),
    [],
  );
  const openDeck = useCallback(
    (page: DeckPage) => setState((s) => ({ ...s, mode: 'deck', deckPage: page })),
    [],
  );

  const collapse = useCallback(() => {
    setState((s) => {
      if (s.mode === 'form') {
        return {
          ...s,
          mode: s.pageControls ? 'deck' : resting(s.activities),
          deckPage: 'controls',
          pageForm: null,
          formTitle: null,
          formWidth: null,
          formMeta: null,
          formContentHeight: null,
        };
      }
      return { ...s, mode: resting(s.activities), toast: null };
    });
  }, []);

  const openForm = useCallback((form: ReactNode, title: string, width?: number, meta?: FormMeta) => {
    setState((s) => ({
      ...s, pageForm: form, formTitle: title, formWidth: width ?? null, formMeta: meta ?? null, formContentHeight: null, mode: 'form',
    }));
  }, []);

  const closeForm = useCallback(() => {
    setState((s) => ({
      ...s,
      pageForm: null,
      formTitle: null,
      formWidth: null,
      formMeta: null,
      formContentHeight: null,
      mode: s.pageControls ? 'deck' : resting(s.activities),
      deckPage: 'controls',
    }));
  }, []);

  const setPage = useCallback((controls: ReactNode | null, label: string | null) => {
    setState((s) => {
      const clearing = controls === null;
      // if the controls page goes away while it's showing, fall back gracefully
      const losingControlsPage = clearing && s.mode === 'deck' && s.deckPage === 'controls';
      return {
        ...s,
        pageControls: controls,
        pageLabel: label,
        // reset the measured height when controls change — the new content
        // will measure itself on mount, and stale heights would cause a flash
        controlsContentHeight: clearing ? null : s.controlsContentHeight,
        mode: clearing && s.mode === 'form' ? resting(s.activities) : s.mode,
        deckPage: losingControlsPage ? 'notifications' : s.deckPage,
        pageForm: clearing ? null : s.pageForm,
        formTitle: clearing ? null : s.formTitle,
        formWidth: clearing ? null : s.formWidth,
        formMeta: clearing ? null : s.formMeta,
        formContentHeight: clearing ? null : s.formContentHeight,
      };
    });
  }, []);

  const setControlsContentHeight = useCallback((h: number | null) => {
    setState((s) =>
      s.controlsContentHeight === h ? s : { ...s, controlsContentHeight: h },
    );
  }, []);

  const setFormContentHeight = useCallback((h: number | null) => {
    setState((s) => (s.formContentHeight === h ? s : { ...s, formContentHeight: h }));
  }, []);

  const notify = useCallback<IslandActions['notify']>((n) => {
    const id = crypto.randomUUID();
    const tone = n.tone ?? 'info';
    setState((s) => ({
      ...s,
      notifications: [
        { id, title: n.title, body: n.body, tone, at: Date.now(), read: false, href: n.href, source: 'session' as const },
        ...s.notifications,
      ].slice(0, 50),
      toast: { id, title: n.title, body: n.body, tone, href: n.href },
      // never interrupt an open deck/form session with a toast morph — und auch
      // die Anrufkarte nicht: die Benachrichtigung landet dann nur in der Liste,
      // statt die Karte für den Toast zu verdrängen.
      mode: s.mode === 'idle' || s.mode === 'live' ? 'toast' : s.mode,
    }));
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setState((s) => ({
        ...s,
        toast: null,
        mode: s.mode === 'toast' ? resting(s.activities) : s.mode,
      }));
    }, TOAST_MS);
  }, []);

  const startActivity = useCallback<IslandActions['startActivity']>((a) => {
    setState((s) => {
      const activities = [
        ...s.activities.filter((x) => x.id !== a.id),
        { id: a.id, label: a.label, progress: a.progress, status: 'running' as const },
      ];
      return { ...s, activities, mode: s.mode === 'idle' ? 'live' : s.mode };
    });
  }, []);

  const updateActivity = useCallback<IslandActions['updateActivity']>((id, patch) => {
    setState((s) => ({
      ...s,
      activities: s.activities.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    }));
  }, []);

  const finishActivity = useCallback<IslandActions['finishActivity']>((id, status, label) => {
    setState((s) => ({
      ...s,
      activities: s.activities.map((a) =>
        a.id === id ? { ...a, status, label: label ?? a.label, progress: 100 } : a,
      ),
    }));
    const t = setTimeout(() => {
      setState((s) => {
        const activities = s.activities.filter((a) => a.id !== id);
        return {
          ...s,
          activities,
          mode: s.mode === 'live' ? resting(activities) : s.mode,
        };
      });
    }, ACTIVITY_LINGER_MS);
    activityTimers.current.push(t);
  }, []);

  const markAllRead = useCallback(() => {
    setState((s) => ({ ...s, notifications: s.notifications.map((n) => ({ ...n, read: true })) }));
  }, []);

  const clearNotifications = useCallback(() => {
    setState((s) => ({ ...s, notifications: [] }));
  }, []);

  const setServerNotifications = useCallback<IslandActions['setServerNotifications']>((items) => {
    setState((s) => ({
      ...s,
      notifications: mergeServerNotifications(s.notifications, items),
    }));
  }, []);

  const zeigeAnruf = useCallback((karte: AnrufKarte) => {
    setState((s) => ({
      ...s,
      anruf: karte,
      // Nach vorn geholt wird nur beim ERSTEN Ereignis eines Anrufs (s.
      // darfAnrufAufreissen): Abnehmen, Auflegen und Statuswechsel aktualisieren
      // die Karte still. Formular und Deck unterbricht sie weiterhin nie.
      mode: darfAnrufAufreissen(s.mode, s.anruf?.id ?? null, karte.id) ? 'anruf' : s.mode,
    }));
  }, []);

  const schliesseAnruf = useCallback(() => {
    setState((s) => ({
      ...s,
      anruf: null,
      mode: s.mode === 'anruf' ? resting(s.activities) : s.mode,
    }));
  }, []);

  // Clear any pending timers on unmount so they don't setState on an unmounted
  // component (the toast timer plus the lingering finishActivity timers).
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    activityTimers.current.forEach(clearTimeout);
  }, []);

  // Stable for the lifetime of the provider — every callback above is useCallback([]).
  const actions = useMemo<IslandActions>(() => ({
    expand, collapse, openSearch, openPage, openDeck, setDeckPage,
    openForm, closeForm, setFormContentHeight, setPage, setControlsContentHeight, notify,
    startActivity, updateActivity, finishActivity,
    markAllRead, clearNotifications, setServerNotifications,
    zeigeAnruf, schliesseAnruf,
    onSearchNavigate,
  }), [
    expand, collapse, openSearch, openPage, openDeck, setDeckPage,
    openForm, closeForm, setFormContentHeight, setPage, setControlsContentHeight, notify,
    startActivity, updateActivity, finishActivity,
    markAllRead, clearNotifications, setServerNotifications,
    zeigeAnruf, schliesseAnruf,
    onSearchNavigate,
  ]);

  return (
    <IslandActionsContext.Provider value={actions}>
      <IslandStateContext.Provider value={state}>
        {children}
      </IslandStateContext.Provider>
    </IslandActionsContext.Provider>
  );
}

/* ------------------------------- hooks ------------------------------- */

/** Subscribe to the island's visual state. Re-renders on every state change. */
export function useIslandState(): IslandState {
  const ctx = useContext(IslandStateContext);
  if (!ctx) throw new Error('useIslandState must be used within <IslandProvider>');
  return ctx;
}

/** Get the island's action methods. Stable — never causes a re-render. */
export function useIslandActions(): IslandActions {
  const ctx = useContext(IslandActionsContext);
  if (!ctx) throw new Error('useIslandActions must be used within <IslandProvider>');
  return ctx;
}

/**
 * Registers a page's control panel with the Dynamic Island. The island renders
 * `controls` as the deck's 'controls' page. Pass primitive `deps` — the controls
 * element is pushed fresh whenever they change. Auto-clears on unmount.
 *
 * Updating and clearing are split into two effects on purpose: a single effect
 * with a cleanup would run `setPage(null)` on every deps change (e.g. every
 * keystroke in a search field), which would briefly drop the controls page and
 * steal focus. The update effect has no cleanup, so the deck's controls page
 * updates in place; clearing only happens when the page truly unmounts.
 */
export function usePageControls(
  controls: ReactNode,
  label: string,
  deps: unknown[],
  // Wenn false, registriert dieser Aufruf NICHTS in den (einzigen) Controls-Slot.
  // Nötig, wenn ein selbst-registrierendes Cockpit-Child (z.B. SocialOverview)
  // den Slot besitzen soll — sonst überschreibt der Parent-Effect (läuft zuletzt)
  // die Child-Controls.
  enabled = true,
) {
  const { setPage } = useIslandActions();
  useEffect(() => {
    if (enabled) setPage(controls, label);
    // enabled MUSS in den Deps sein: Beim Flip false→true (z. B. Rückkehr von einer
    // Detailseite, deren eigene Controls beim Unmount setPage(null) aufgerufen haben)
    // muss dieser Effect neu feuern und die Controls wieder registrieren — sonst
    // bleibt die Pille leer, bis sich zufällig ein anderer Dep ändert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);
  useEffect(() => {
    if (!enabled) return;
    return () => setPage(null, null);
  }, [setPage, enabled]);
}
